import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// VISIT DRAFTS (round 9, owner 24 Sep 2026): a doctor's typed notes and
// prescription must never be lost when they look away from the visit — to the
// patient's record, another tab, a closed laptop. So what they type is saved
// SERVER-SIDE as a draft, per section, a couple of seconds after they stop
// typing, and handed back when the visit is opened again.
//
// A draft is the doctor's own scratch copy, not the visit's record: the real
// note/prescription endpoints are still what "Save" commits, and a Save
// deletes the matching draft (the server does it too, best-effort).
//
//   GET    /appointment-drafts/:appointmentId           → { drafts: { [section]: { content, updatedAt } } }
//   PUT    /appointment-drafts/:appointmentId/:section  { content } → { section, updatedAt }
//   DELETE /appointment-drafts/:appointmentId/:section  → 200 (idempotent)
//   POST   /appointment-drafts/:appointmentId/commit
//          { notes: { [fieldId]: text }, prescription: { create: [...], update: [...] },
//            checks: { [setId]: { [fieldKey]: value } } }
//          → 200 { notes, prescription: { created, updated }, checks: [...], draftsDeleted }
//          "Save & complete" (owner, 24 Sep 2026): EVERY pending section in
//          ONE transaction — all written or none (a refusal names the
//          section, and `field` for a check value) — and the server deletes
//          the drafts of the sections it WROTE, and only those (check sets,
//          26 Sep 2026: a notes-only commit must not wipe a pending check).
//
// Sections: `notes.<fieldId>` for a note box (content: the string),
// `prescription` (content: the pending rows array) and `checks.<setId>` for a
// check set (content: the `{ fieldKey: value }` map the commit takes).
//
// The slice holds ONE appointment's drafts at a time — the one on screen. An
// answer that arrives for a visit the doctor has already left is dropped, so
// visit A's draft can never be restored into visit B's box.

export const DRAFT_DEBOUNCE_MS = 2000;

export const noteDraftSection = (fieldId) => `notes.${fieldId}`;
export const PRESCRIPTION_DRAFT_SECTION = 'prescription';

// Mirrored from Backend/utils/appointmentDrafts.js (and the Pro-App's
// visitDrafts.jsx): a check set's draft section is `checks.<setId>`.
const CHECK_PREFIX = 'checks.';
export const checkSection = (setId) => `${CHECK_PREFIX}${setId}`;
export const isCheckSection = (section) => typeof section === 'string'
    && section.startsWith(CHECK_PREFIX) && section.length > CHECK_PREFIX.length;
export const setIdOfSection = (section) => (isCheckSection(section) ? section.slice(CHECK_PREFIX.length) : null);

const serializeError = (error) => {
    const data = error?.response?.data;
    return {
        code: data?.code || null,
        message: data?.message || error?.message || 'Request failed',
        status: error?.response?.status || null,
    };
};

export const fetchDrafts = createAsyncThunk(
    'appointmentDraft/fetch',
    async ({ appointmentId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/appointment-drafts/${encodeURIComponent(appointmentId)}`);
            return { drafts: res.data?.drafts || {} };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        // The notes and the prescription both ask on mount (a child's effect
        // runs before its parent's); one request answers both.
        condition: ({ appointmentId, force = false }, { getState }) => {
            if (!appointmentId) return false;
            const s = getState().appointmentDraft;
            if (force || !s) return true;
            return !(s.appointmentId === appointmentId && (s.loading || s.loaded));
        },
    }
);

export const saveDraft = createAsyncThunk(
    'appointmentDraft/save',
    async ({ appointmentId, section, content }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(
                `${baseURL}/appointment-drafts/${encodeURIComponent(appointmentId)}/${encodeURIComponent(section)}`,
                { content }
            );
            return {
                section: res.data?.section || section,
                // Fall back to "now" so the chip always has a time to print.
                updatedAt: res.data?.updatedAt || new Date().toISOString(),
            };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const deleteDraft = createAsyncThunk(
    'appointmentDraft/delete',
    async ({ appointmentId, section }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            await axiosInstance.delete(
                `${baseURL}/appointment-drafts/${encodeURIComponent(appointmentId)}/${encodeURIComponent(section)}`
            );
            return { section };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const commitDrafts = createAsyncThunk(
    'appointmentDraft/commit',
    async ({ appointmentId, body }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(
                `${baseURL}/appointment-drafts/${encodeURIComponent(appointmentId)}/commit`,
                body
            );
            return res.data || {};
        } catch (error) {
            // The raw answer ({ code, section, index?, message }) so the
            // screen can print the server's own (possibly bilingual) sentence
            // and point at the section it names.
            const data = error?.response?.data;
            return rejectWithValue(data && typeof data === 'object'
                ? { ...data, status: error?.response?.status || null }
                : serializeError(error));
        }
    }
);

const appointmentDraftSlice = createSlice({
    name: 'appointmentDraft',
    initialState: {
        appointmentId: null,
        drafts: {},
        loading: false,
        loaded: false,
        error: null,
        // Draft PUTs still on the wire: requestId → { appointmentId, section,
        // stale }. A PUT sent BEFORE a commit (or a section's own Save / a
        // discard) retired the draft must not bring it back when its answer
        // lands afterwards: that stale text would be "restored" into the box
        // and the visit would read as unsaved again (owner, 24 Sep 2026).
        inflightSaves: {},
    },
    reducers: {
        clearDrafts(state) {
            state.appointmentId = null;
            state.drafts = {};
            state.inflightSaves = {};
            state.loading = false;
            state.loaded = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDrafts.pending, (state, action) => {
                const { appointmentId } = action.meta.arg;
                if (state.appointmentId !== appointmentId) state.drafts = {};
                state.appointmentId = appointmentId;
                state.loading = true;
                state.loaded = false;
                state.error = null;
            })
            .addCase(fetchDrafts.fulfilled, (state, action) => {
                if (state.appointmentId !== action.meta.arg.appointmentId) return;
                state.loading = false;
                state.loaded = true;
                state.drafts = action.payload.drafts;
            })
            .addCase(fetchDrafts.rejected, (state, action) => {
                if (state.appointmentId !== action.meta.arg.appointmentId) return;
                // A failed read is "no drafts to restore", never a blocked
                // screen: the doctor can still type and save as before.
                state.loading = false;
                state.loaded = true;
                state.drafts = {};
                state.error = action.payload || null;
            })
            .addCase(saveDraft.pending, (state, action) => {
                const { appointmentId, section } = action.meta.arg;
                if (!state.inflightSaves) state.inflightSaves = {};
                state.inflightSaves[action.meta.requestId] = { appointmentId, section, stale: false };
            })
            .addCase(saveDraft.fulfilled, (state, action) => {
                const { appointmentId, section, content } = action.meta.arg;
                const inflight = state.inflightSaves?.[action.meta.requestId];
                if (state.inflightSaves) delete state.inflightSaves[action.meta.requestId];
                if (inflight?.stale) return;
                if (state.appointmentId !== appointmentId) return;
                state.drafts[section] = { content, updatedAt: action.payload.updatedAt };
            })
            .addCase(saveDraft.rejected, (state, action) => {
                if (state.inflightSaves) delete state.inflightSaves[action.meta.requestId];
            })
            .addCase(deleteDraft.pending, (state, action) => {
                // Optimistic: a discarded or committed draft must not be
                // restored by a re-render while the DELETE is in flight.
                const { appointmentId, section } = action.meta.arg;
                Object.values(state.inflightSaves || {}).forEach((p) => {
                    if (p.appointmentId === appointmentId && p.section === section) p.stale = true;
                });
                if (state.appointmentId !== appointmentId) return;
                delete state.drafts[section];
            })
            .addCase(commitDrafts.fulfilled, (state, action) => {
                // The server deleted the drafts it committed: none of those
                // may be restored — not even one whose PUT is still on the
                // wire. Since check sets (26 Sep 2026) it names them in
                // `draftsDeleted` and leaves every other pending draft alone;
                // an answer without the list (an older backend) deleted
                // every draft of the visit.
                const { appointmentId } = action.meta.arg;
                const deleted = Array.isArray(action.payload?.draftsDeleted) ? action.payload.draftsDeleted : null;
                Object.values(state.inflightSaves || {}).forEach((p) => {
                    if (p.appointmentId === appointmentId && (!deleted || deleted.includes(p.section))) p.stale = true;
                });
                if (state.appointmentId !== appointmentId) return;
                if (!deleted) {
                    state.drafts = {};
                    return;
                }
                deleted.forEach((section) => { delete state.drafts[section]; });
            });
    },
});

export const { clearDrafts } = appointmentDraftSlice.actions;
export default appointmentDraftSlice.reducer;
